import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Edit, Eye, BarChart, CheckCircle, FileText } from 'lucide-react';
import PolicyForm from '../../components/PolicyForm';
import PolicyDetailView from '../../components/PolicyDetailView';
import PolicyAnalysisEditor from '../../components/PolicyAnalysisEditor';
import StepByStepPolicyAnalyzer from '../../components/StepByStepPolicyAnalyzer';
import { AUTH_CONFIG } from '../../config';
import AdminLayout from '../../components/AdminLayout';

const EditPolicy = () => {
  const { id } = useParams();
  console.log('EditPolicy component mounted with id:', id);
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('details'); // details, preview, analysis, analyze
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [analyzeMode, setAnalyzeMode] = useState(false);
  const [analysisCompleted, setAnalysisCompleted] = useState(false);
  
  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Use the shared AUTH_CONFIG helper instead of direct fetch
        const isAuth = await AUTH_CONFIG.isAuthenticated();
        console.log('Auth check completed, isAuthenticated:', isAuth);
        setIsAuthenticated(isAuth);
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthChecking(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // Force isAuthenticated to true if we detect an auth error but have valid local token
  // This is a fallback to handle cases where the auth check may be failing
  useEffect(() => {
    console.log(`EditPolicy: Auth fallback check - isAuthenticated: ${isAuthenticated}, authChecking: ${authChecking}`);
    
    if (!isAuthenticated && !authChecking) {
      const token = localStorage.getItem('authToken');
      console.log(`EditPolicy: Token exists in localStorage: ${!!token}`);
      
      if (token) {
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          console.log(`EditPolicy: Token data:`, tokenData);
          
          if (tokenData.exp * 1000 > Date.now()) {
            console.log('EditPolicy: Local fallback auth - Setting isAuthenticated to true');
            setIsAuthenticated(true);
          } else {
            console.log(`EditPolicy: Token expired - exp: ${new Date(tokenData.exp * 1000)}, now: ${new Date()}`);
          }
        } catch (e) {
          console.error('EditPolicy: Fallback auth error:', e);
        }
      }
    }
  }, [isAuthenticated, authChecking]);
  
  // Fetch policy data on component mount
  useEffect(() => {
    const fetchPolicy = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log(`EditPolicy: Fetching policy with ID ${id}`);
        const response = await fetch(`/api/policies/${id}`);
        
        if (!response.ok) {
          throw new Error(`Error fetching policy: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('EditPolicy: Received policy data:', data);
        
        if (data.success) {
          setPolicy(data.policy);
          console.log(`EditPolicy: Set policy with ID ${data.policy.id}, status: ${data.policy.status}`);
          
          // If policy status is 'analyzed', set analysisCompleted to true
          if (data.policy.status === 'analyzed') {
            setAnalysisCompleted(true);
          }
          
          // Check for analyzeTab query parameter and set analyze mode
          const searchParams = new URLSearchParams(window.location.search);
          if (searchParams.get('analyzeTab') === 'true') {
            console.log('Setting analyze mode to true based on URL parameter');
            setAnalyzeMode(true);
          }
        } else {
          throw new Error(data.error || 'Failed to fetch policy');
        }
      } catch (err) {
        console.error('Error fetching policy:', err);
        setError(err.message || 'Failed to load policy');
      } finally {
        setLoading(false);
      }
    };
    
    console.log(`EditPolicy: Ready to fetch? id: ${id}, isAuthenticated: ${isAuthenticated}, authChecking: ${authChecking}`);
    
    if (id && isAuthenticated) {
      console.log(`EditPolicy: Initiating fetchPolicy() for id: ${id}`);
      fetchPolicy();
    } else {
      console.log(`EditPolicy: Cannot fetch - id: ${id}, isAuthenticated: ${isAuthenticated}`);
    }
  }, [id, isAuthenticated]);
  
  // Handle form submission for editing
  const handleSubmit = async (policyData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch(`/api/policies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(policyData)
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        
        if (contentType.includes('text/html')) {
          throw new Error(`Server error ${response.status}: ${response.statusText}`);
        }
        
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to update policy');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh policy data
        const refreshResponse = await fetch(`/api/policies/${id}`);
        const refreshData = await refreshResponse.json();
        
        if (refreshData.success) {
          setPolicy(refreshData.policy);
          setSubmitError(null);
        }
      } else {
        throw new Error(data.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error updating policy:', error);
      setSubmitError(error.message || 'Failed to update policy');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle policy deletion
  const handleDelete = async (policyId) => {
    try {
      const response = await fetch(`/api/policies/${policyId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Error deleting policy: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Navigate back to policies list
        navigate('/admin/policies');
      } else {
        throw new Error(data.error || 'Failed to delete policy');
      }
    } catch (error) {
      console.error('Error deleting policy:', error);
      setSubmitError(error.message || 'Failed to delete policy');
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    navigate('/admin/policies');
  };
  
  // Handle toggling policy status
  const handleToggleStatus = async () => {
    if (!policy) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Determine new status based on current status
      let newStatus;
      let action;
      
      switch (policy.status) {
        case 'draft':
          newStatus = 'analyzed';
          action = 'marking as analyzed';
          break;
        case 'analyzed':
          newStatus = 'live';
          action = 'publishing';
          break;
        case 'live':
          newStatus = 'analyzed';
          action = 'unpublishing';
          break;
        default:
          newStatus = 'analyzed';
          action = 'updating status';
      }
      
      const response = await fetch(`/api/policies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          // If publishing, set published_at timestamp
          ...(newStatus === 'live' ? { published_at: new Date().toISOString() } : {})
        })
      });
      
      if (!response.ok) {
        throw new Error(`Error ${action} policy: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh policy data
        const refreshResponse = await fetch(`/api/policies/${id}`);
        const refreshData = await refreshResponse.json();
        
        if (refreshData.success) {
          setPolicy(refreshData.policy);
          setSubmitError(null);
        }
      } else {
        throw new Error(data.error || `Failed to ${action} policy`);
      }
    } catch (error) {
      console.error(`Error toggling policy status:`, error);
      setSubmitError(error.message || 'Failed to update policy status');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle analysis completion
  const handleAnalysisComplete = async (result) => {
    setAnalysisCompleted(true);
    setAnalyzeMode(false);
    setActiveTab('preview');
    
    // Refresh policy data to get updated analysis results
    try {
      const response = await fetch(`/api/policies/${id}`);
      const data = await response.json();
      
      if (data.success) {
        setPolicy(data.policy);
      }
    } catch (error) {
      console.error('Error refreshing policy data:', error);
    }
  };
  
  // Content to render when authenticated and data is loaded
  const authContent = (
    <div className="container mx-auto px-4 max-w-5xl">
      <div className="mb-6">
        <Link to="/admin/policies" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to policy management
        </Link>
      </div>
      
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {loading ? 'Loading Policy...' : policy ? policy.title : 'Policy Not Found'}
            </h1>
            {policy && (
              <p className="mt-1 text-sm text-gray-500">
                ID: {policy.id} • Status: {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                {policy.status === 'live' && policy.published_at && (
                  <> • Published: {new Date(policy.published_at).toLocaleDateString()}</>
                )}
              </p>
            )}
          </div>
          
          {policy && (
            <div className="mt-4 md:mt-0">
              <button
                onClick={handleToggleStatus}
                disabled={isSubmitting}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  policy.status === 'live' 
                    ? 'bg-amber-600 hover:bg-amber-700' 
                    : policy.status === 'analyzed'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                } ${
                  isSubmitting ? 'opacity-75 cursor-not-allowed' : 'focus:outline-none'
                }`}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                {policy.status === 'live' 
                  ? 'Unpublish Policy' 
                  : policy.status === 'analyzed'
                  ? 'Publish Policy'
                  : 'Mark as Analyzed'}
              </button>
            </div>
          )}
        </div>
        
        <div className="px-6 py-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-gray-600">Loading policy data...</p>
            </div>
          ) : error ? (
            <div className="py-8 bg-red-50 rounded-lg text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading Policy</h2>
              <p className="text-red-600 mb-6">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              >
                Try Again
              </button>
            </div>
          ) : policy ? (
            <>
              {/* Submit error message */}
              {submitError && (
                <div className="p-4 mb-6 bg-red-50 border-l-4 border-red-500 text-red-700">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    <span>{submitError}</span>
                  </div>
                </div>
              )}
              
              {/* Action tabs */}
              {!analyzeMode && (
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setActiveTab('details')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'details'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Edit className="inline-block h-4 w-4 mr-2" />
                      Edit Policy
                    </button>
                    
                    <button
                      onClick={() => setActiveTab('analysis')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'analysis'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <FileText className="inline-block h-4 w-4 mr-2" />
                      Edit Analysis
                    </button>
                    
                    <button
                      onClick={() => setActiveTab('preview')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'preview'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Eye className="inline-block h-4 w-4 mr-2" />
                      Preview
                    </button>
                    
                    <button
                      onClick={() => {
                        if (policy.status === 'draft' || !analysisCompleted) {
                          setAnalyzeMode(true);
                        } else {
                          setActiveTab('preview');
                        }
                      }}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'analyze'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <BarChart className="inline-block h-4 w-4 mr-2" />
                      {policy.status === 'draft' || !analysisCompleted ? 'Analyze' : 'Analysis Complete'}
                    </button>
                  </nav>
                </div>
              )}
              
              {/* Tab content */}
              {activeTab === 'details' && !analyzeMode && (
                <PolicyForm 
                  policy={policy}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  onDelete={handleDelete}
                  isLoading={isSubmitting}
                  submitButtonText="Save Changes"
                  showDelete={true}
                />
              )}
              
              {activeTab === 'analysis' && !analyzeMode && (
                <PolicyAnalysisEditor
                  policy={policy}
                  onSave={async (updatedData) => {
                    setIsSubmitting(true);
                    setSubmitError(null);
                    
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          scores: updatedData
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to update policy analysis');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      setSubmitError(err.message || 'Failed to update policy analysis');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  onCancel={() => setActiveTab('preview')}
                  isLoading={isSubmitting}
                  onUpdateTactic={async (updatedTactic) => {
                    try {
                      console.log("Updating tactic:", updatedTactic);
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          tactics: [{ ...updatedTactic, action: 'update' }]
                        })
                      });
                      
                      if (!response.ok) {
                        const errorText = await response.text();
                        console.error("Server response:", errorText);
                        throw new Error(`Failed to update tactic: ${response.status} ${response.statusText}`);
                      }
                    } catch (err) {
                      console.error('Error updating tactic:', err);
                    }
                  }}
                  onDeleteTactic={async (tacticId) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          tactics: [{ id: tacticId, action: 'delete' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to delete tactic');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      console.error('Error deleting tactic:', err);
                    }
                  }}
                  onAddTactic={async (newTactic) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          tactics: [{ ...newTactic, action: 'add' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to add tactic');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      console.error('Error adding tactic:', err);
                    }
                  }}
                  onUpdateConstitutionalIssue={async (updatedIssue) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          constitutional_issues: [{ ...updatedIssue, action: 'update' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to update constitutional issue');
                      }
                    } catch (err) {
                      console.error('Error updating constitutional issue:', err);
                    }
                  }}
                  onDeleteConstitutionalIssue={async (issueId) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          constitutional_issues: [{ id: issueId, action: 'delete' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to delete constitutional issue');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      console.error('Error deleting constitutional issue:', err);
                    }
                  }}
                  onAddConstitutionalIssue={async (newIssue) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          constitutional_issues: [{ ...newIssue, action: 'add' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to add constitutional issue');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      console.error('Error adding constitutional issue:', err);
                    }
                  }}
                  onUpdatePrecedent={async (updatedPrecedent) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          precedents: [{ ...updatedPrecedent, action: 'update' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to update precedent');
                      }
                    } catch (err) {
                      console.error('Error updating precedent:', err);
                    }
                  }}
                  onDeletePrecedent={async (precedentId) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          precedents: [{ id: precedentId, action: 'delete' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to delete precedent');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      console.error('Error deleting precedent:', err);
                    }
                  }}
                  onAddPrecedent={async (newPrecedent) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          precedents: [{ ...newPrecedent, action: 'add' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to add precedent');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      console.error('Error adding precedent:', err);
                    }
                  }}
                  onUpdateExcerpt={async (updatedExcerpt) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          excerpts: [{ ...updatedExcerpt, action: 'update' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to update excerpt');
                      }
                    } catch (err) {
                      console.error('Error updating excerpt:', err);
                    }
                  }}
                  onDeleteExcerpt={async (excerptId) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          excerpts: [{ id: excerptId, action: 'delete' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to delete excerpt');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      console.error('Error deleting excerpt:', err);
                    }
                  }}
                  onAddExcerpt={async (newExcerpt) => {
                    try {
                      const response = await fetch(`/api/policies/update-analysis`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          policy_id: policy.id,
                          excerpts: [{ ...newExcerpt, action: 'add' }]
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to add excerpt');
                      }
                      
                      // Refresh the policy data
                      const refreshResponse = await fetch(`/api/policies/${id}`);
                      const refreshData = await refreshResponse.json();
                      
                      if (refreshData.success) {
                        setPolicy(refreshData.policy);
                      }
                    } catch (err) {
                      console.error('Error adding excerpt:', err);
                    }
                  }}
                />
              )}
              
              {activeTab === 'preview' && !analyzeMode && (
                <div className="policy-preview-container">
                  <div className="pb-4 border-b border-gray-200 mb-4 flex space-x-4">
                    <button
                      onClick={() => setActiveTab('details')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit Policy
                    </button>
                    
                    <button
                      onClick={() => setActiveTab('analysis')}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Edit Analysis Data
                    </button>
                  </div>
                  <PolicyDetailView policyId={policy.id} isAdmin={true} disableEditButton={true} />
                </div>
              )}
              
              {/* Analyze mode */}
              {analyzeMode && (
                <div>
                  <div className="mb-4 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900">Step-by-Step Policy Analysis</h2>
                    <button
                      onClick={() => setAnalyzeMode(false)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Cancel Analysis
                    </button>
                  </div>
                  
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          This process will analyze the policy text stored in the database for policy ID {policy.id}.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <StepByStepPolicyAnalyzer
                    policyId={policy.id}
                    onAnalysisComplete={handleAnalysisComplete}
                    isWorkflow={true}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="py-8 bg-gray-50 rounded-lg text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Policy Not Found</h2>
              <p className="text-gray-500 mb-6">
                The policy you are looking for does not exist or has been deleted.
              </p>
              <Link
                to="/admin/policies"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              >
                Back to Policy List
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Loading spinner while checking auth
  const loadingContent = (
    <div className="text-center py-8">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      <p className="mt-2 text-gray-600">Checking authentication...</p>
    </div>
  );

  // Not authenticated message
  const unauthContent = (
    <div className="py-8 bg-red-50 rounded-lg text-center">
      <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
      <h2 className="text-xl font-bold text-red-800 mb-2">Authentication Required</h2>
      <p className="text-red-600 mb-6">
        You must be logged in as an administrator to access this area.
      </p>
      <Link
        to="/admin"
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
      >
        Go to Admin Login
      </Link>
    </div>
  );
  
  return (
    <AdminLayout>
      {authChecking ? loadingContent : !isAuthenticated ? unauthContent : authContent}
    </AdminLayout>
  );
};

export default EditPolicy;